// PRC-029: Metodología con la plantilla de lectura (mismo contenido que la
// versión anterior, ordenado como documento y en frase normal).
import React from 'react';
import DocPage, { DocCode, DocH3, DocList, DocNote, DocP } from '../modules/pricingJourney/components/DocPage';

const UPDATED = '3 de noviembre de 2026';

const SECTIONS = [
  {
    id: 'datos',
    title: '1. Carga y limpieza de datos',
    content: (
      <>
        <DocH3>Sin suposiciones</DocH3>
        <DocP>
          La base de una estrategia de precios sólida es la integridad del dato. PROA Pricing no «adivina» la estructura de tu información: al cargarla, cada columna se vincula de forma explícita con nuestro estándar.
        </DocP>
        <DocList items={[
          <><b>Identidad:</b> los registros sin cliente o producto identificable se descartan; no se pueden trazar y solo añaden ruido.</>,
          <><b>Versiones:</b> si un producto o cliente aparece duplicado, se conserva su última versión válida.</>,
        ]} />

        <DocH3>Cómo se calcula el margen</DocH3>
        <DocP>El sistema trabaja con el margen bruto sobre coste:</DocP>
        <DocCode>{'Margen sobre coste = (precio de venta − coste total) / coste total'}</DocCode>
        <DocP>
          El coste unitario que se carga es el suelo de la optimización. Si ese coste incluye comisiones o logística, el margen resultante ya las descuenta.
        </DocP>

        <DocH3>Alcance y responsabilidad</DocH3>
        <DocList items={[
          <><b>Calidad de los datos:</b> los modelos multiplican la eficiencia, no crean la realidad. La calidad de una recomendación depende directamente de la de los datos.</>,
          <><b>Productos sin historia:</b> el sistema no inventa ventas. Si un producto no tiene histórico ni se parece a otros, se aplican reglas prudentes en lugar de especular.</>,
        ]} />

        <DocH3>Estructura de datos</DocH3>
        <DocP>
          Hay campos obligatorios, sin los que el sistema no funciona, y campos opcionales. Cuantos más atributos opcionales se aporten, más precisa es la optimización.
        </DocP>
        <DocList items={[
          <><b>Clientes:</b> obligatorio, ID de cliente. Opcionales: sector, ubicación, tamaño…</>,
          <><b>Productos:</b> obligatorios, ID de producto y coste estándar. Opcionales: familia, marca, categoría…</>,
          <><b>Histórico de ventas:</b> obligatorios, fecha, ID de producto, ID de cliente, cantidad y precio.</>,
        ]} />
        <DocNote>Para entrenar con garantías estadísticas, el sistema necesita al menos 24 meses de histórico de ventas.</DocNote>

        <DocH3>Reconstrucción del coste histórico</DocH3>
        <DocP>Para calcular el margen de cada venta pasada se usa el dato de coste más fiable disponible en ese momento, en este orden:</DocP>
        <DocList items={[
          <><b>Coste de la venta:</b> el que viene en la propia línea de venta.</>,
          <><b>Coste histórico:</b> si la venta no lo trae, el del módulo de costes históricos.</>,
          <><b>Coste actual:</b> como alternativa final, el coste estándar actual de la ficha del producto.</>,
        ]} />
      </>
    ),
  },
  {
    id: 'optimizacion',
    title: '2. Motor de optimización',
    content: (
      <>
        <DocH3>Aprender de las mejores ventas</DocH3>
        <DocP>
          El entrenamiento no busca promedios, sino los casos de máxima eficiencia: recorre el histórico operación a operación para aislar los momentos en que se consiguió más margen sin perder la venta. Después proyecta esas condiciones sobre el resto del catálogo.
        </DocP>

        <DocH3>Una base y un techo</DocH3>
        <DocP>
          El precio nunca es un valor inventado. El sistema calcula primero una base: el margen que pondrías hoy a esa venta. Sobre esa base calcula un techo de potencial. La diferencia entre ambos es el rango de oportunidad, el espacio donde se puede mejorar sin romper la realidad del mercado.
        </DocP>

        <DocH3>Estrategia</DocH3>
        <DocP>
          Con el rango de oportunidad calculado, la estrategia elegida decide qué parte de esa oportunidad se intenta capturar en cada venta. Tú controlas siempre lo agresivo que es el modelo.
        </DocP>

        <DocH3>Capas de seguridad</DocH3>
        <DocP>
          Además de frenar subidas cuando detecta sensibilidad al precio, el sistema mide en cada recomendación su confianza estadística. Si es baja, reduce mucho el movimiento permitido: prioriza conservar el volumen sobre capturar un margen incierto.
        </DocP>
      </>
    ),
  },
  {
    id: 'demanda',
    title: '3. Previsión de demanda',
    content: (
      <>
        <DocH3>De lo general a lo concreto</DocH3>
        <DocP>
          La previsión es jerárquica: la suma de las previsiones de cada producto cuadra con la tendencia general, para que los totales sean sólidos. El modelo tiene en cuenta la inflación interna y la presión de precios; entiende cada venta como una función del precio y del contexto.
        </DocP>

        <DocH3>Cliente a cliente</DocH3>
        <DocP>
          La demanda futura no se reparte: se reconstruye cliente a cliente, según la probabilidad de compra de cada cuenta activa por su frecuencia reciente. Los clientes con patrones erráticos o inactivos durante mucho tiempo se excluyen de la previsión base.
        </DocP>

        <DocH3>Elasticidad</DocH3>
        <DocP>
          No hay escenarios de «solo margen»: cualquier cambio de precio recalcula el volumen previsto. Si se sube el margen unitario, se reducen las unidades según la sensibilidad histórica del cliente, y el resultado final descuenta ese riesgo.
        </DocP>
        <DocCode>{`Si el precio sube:
  1. Medir la sensibilidad del cliente
  2. Reducir el volumen previsto
Margen final = nuevo margen × menor volumen
Se aplica solo si el margen final es mayor que el actual`}</DocCode>
      </>
    ),
  },
];

const MethodologyPage: React.FC = () => (
  <DocPage
    eyebrow="Metodología"
    title="Cómo decide PROA Pricing"
    updated={UPDATED}
    intro="Los principios, límites y pasos con los que la plataforma trata tus datos y calcula sus recomendaciones."
    sections={SECTIONS}
  />
);

export default MethodologyPage;
